import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface LiveRegionContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

const LiveRegionContext = createContext<LiveRegionContextType | null>(null);

interface LiveRegionProviderProps {
  children: ReactNode;
}

/**
 * Provider for screen reader announcements
 * Provides a way to announce messages to assistive technologies
 */
export function LiveRegionProvider({ children }: LiveRegionProviderProps) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (priority === 'assertive') {
      setAssertiveMessage(message);
      // Clear after announcement
      setTimeout(() => setAssertiveMessage(''), 1000);
    } else {
      setPoliteMessage(message);
      // Clear after announcement
      setTimeout(() => setPoliteMessage(''), 1000);
    }
  }, []);

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}

      {/* ARIA live regions for screen readers */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
}

/**
 * Hook to announce messages to screen readers
 * @example
 * const announce = useAnnounce();
 * announce('Item added to cart'); // Polite announcement
 * announce('Error: connection lost', 'assertive'); // Urgent announcement
 */
export function useAnnounce(): (message: string, priority?: 'polite' | 'assertive') => void {
  const context = useContext(LiveRegionContext);

  if (!context) {
    // Return a no-op function if used outside provider
    // This allows the hook to be used safely without the provider
    return () => {};
  }

  return context.announce;
}
